import { messaging } from '@/api/engine';
import { notifyUnauthorized } from './auth';
import { SEITIATE_ENGINE_URL } from './env';

export class EngineHttpError extends Error {
  status: number;
  /** Stable machine code from a closed `{error:{code,...}}` envelope, when present. */
  code?: string;
  /** Safe details from that envelope. Never contains prose (contract §11). */
  details?: Record<string, unknown>;
  /** Server-issued correlation id, when the envelope carries one. */
  requestId?: string;

  constructor(
    status: number,
    message: string,
    envelope?: { code?: string; details?: Record<string, unknown>; requestId?: string },
  ) {
    super(message);
    this.name = 'EngineHttpError';
    this.status = status;
    if (envelope?.code) this.code = envelope.code;
    if (envelope?.details) this.details = envelope.details;
    if (envelope?.requestId) this.requestId = envelope.requestId;
  }
}

/** A response together with the headers a caller needs, e.g. a strong `ETag`. */
export interface EngineResponse<T> {
  status: number;
  body: T;
  headers: Headers;
}

/**
 * Read the closed error envelope both desk contracts define:
 * `{"error": {"code", "message", "request_id", "details"}}`
 * (`desk-document.v1.api.md` §11, `desk-project.v1.api.md` §8).
 *
 * The desk needs the machine `code` and the safe `details` — a 409 carries the
 * current revision and digests, a 423 carries the unlock path — and a generic
 * message string throws that away. Bodies that are not this envelope fall back to
 * the existing human-readable reader, so nothing else on the engine changes shape.
 */
function readErrorEnvelope(raw: string): {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  requestId?: string;
} | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { error?: unknown };
    const error = parsed?.error;
    if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
    const entry = error as Record<string, unknown>;
    if (typeof entry.code !== 'string') return null;
    return {
      code: entry.code,
      message: typeof entry.message === 'string' ? entry.message : undefined,
      details:
        entry.details && typeof entry.details === 'object' && !Array.isArray(entry.details)
          ? (entry.details as Record<string, unknown>)
          : undefined,
      requestId: typeof entry.request_id === 'string' ? entry.request_id : undefined,
    };
  } catch {
    return null;
  }
}

export type EngineRequestFailureKind =
  | 'server'
  | 'transport'
  | 'ambiguous-transport'
  | 'abort'
  | 'unknown';

export interface EngineRequestFailureClassification {
  kind: EngineRequestFailureKind;
  isTransportFailure: boolean;
  hasStrongTransportEvidence: boolean;
}

export interface EngineStreamEvent<T = unknown> {
  event: string;
  data: T;
}

const STRONG_FETCH_TRANSPORT_PATTERNS = [
  /failed to fetch/i,
  /load failed/i,
  /networkerror/i,
  /network request failed/i,
  /fetch failed/i,
  /internet connection appears to be offline/i,
  /network connection was lost/i,
];

function errorSignalText(error: Error): string {
  const parts = [error.name, error.message];
  const cause = (error as Error & { cause?: unknown }).cause;

  if (typeof cause === 'string') {
    parts.push(cause);
  } else if (cause instanceof Error) {
    parts.push(cause.name, cause.message);
  }

  return parts.filter(Boolean).join(' ').trim();
}

export function classifyEngineRequestFailure(error: unknown): EngineRequestFailureClassification {
  if (error instanceof EngineHttpError) {
    return {
      kind: 'server',
      isTransportFailure: false,
      hasStrongTransportEvidence: false,
    };
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return {
      kind: 'abort',
      isTransportFailure: false,
      hasStrongTransportEvidence: false,
    };
  }

  if (error instanceof TypeError) {
    const navigatorOffline =
      typeof navigator !== 'undefined' &&
      'onLine' in navigator &&
      navigator.onLine === false;
    const transportText = errorSignalText(error);
    const hasStrongTransportEvidence =
      navigatorOffline ||
      STRONG_FETCH_TRANSPORT_PATTERNS.some((pattern) => pattern.test(transportText));

    return {
      kind: hasStrongTransportEvidence ? 'transport' : 'ambiguous-transport',
      isTransportFailure: hasStrongTransportEvidence,
      hasStrongTransportEvidence,
    };
  }

  return {
    kind: 'unknown',
    isTransportFailure: false,
    hasStrongTransportEvidence: false,
  };
}

function mergeHeaders(extra?: HeadersInit): HeadersInit {
  const base = messaging.authHeaders() as Record<string, string>;
  if (!extra) return base;
  if (extra instanceof Headers) {
    const merged = new Headers(base);
    extra.forEach((value, key) => merged.set(key, value));
    return merged;
  }
  if (Array.isArray(extra)) {
    const merged = new Headers(base);
    extra.forEach(([key, value]) => merged.set(key, value));
    return merged;
  }
  return { ...base, ...extra };
}

/**
 * Options a caller may add to the one engine fetch path.
 *
 * `base` is the only knob: the Writing Desk's store client passes
 * `SEITIATE_DESK_ENGINE_URL` so that its `/desk/*` family — and nothing else —
 * follows the desk engine (rung M0). Omitted, everything resolves against the
 * engine exactly as before.
 */
export interface EngineFetchOptions {
  base?: string;
}

export function engineUrl(path: string, base: string = SEITIATE_ENGINE_URL): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/api/') ? path : `/api/v1${path.startsWith('/') ? path : `/${path}`}`;
  return `${base}${normalized}`;
}

export async function engineFetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(engineUrl(path), {
    ...init,
    headers: mergeHeaders(init.headers),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    const message = readableErrorFromBody(raw) || response.statusText;
    if (response.status === 401) {
      notifyUnauthorized(message);
    }
    throw new EngineHttpError(response.status, message);
  }

  return (await response.json()) as T;
}

/**
 * Like {@link engineFetchJson}, but returns the response headers alongside the
 * body and preserves a closed error envelope's `code` and `details`.
 *
 * The desk store contracts need both: tree mutations are guarded by the strong
 * `ETag` header, and 409/423 handling is driven by the error `code` rather than
 * by matching on a message. Auth headers, URL construction, and the 401 handoff
 * are the same as `engineFetchJson` — this is one more shape on the one engine
 * fetch path, not a second path. `options.base` lets the desk store aim that
 * same path at its own engine.
 */
export async function engineFetchEnvelope<T>(
  path: string,
  init: RequestInit = {},
  options: EngineFetchOptions = {},
): Promise<EngineResponse<T>> {
  const response = await fetch(engineUrl(path, options.base), {
    ...init,
    headers: mergeHeaders(init.headers),
  });

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    const envelope = readErrorEnvelope(raw);
    const message = envelope?.message || readableErrorFromBody(raw) || response.statusText;
    if (response.status === 401) {
      notifyUnauthorized(message);
    }
    throw new EngineHttpError(response.status, message, envelope ?? undefined);
  }

  if (response.status === 204) {
    return { status: response.status, body: undefined as T, headers: response.headers };
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) {
    return { status: response.status, body: undefined as T, headers: response.headers };
  }

  return {
    status: response.status,
    body: (await response.json()) as T,
    headers: response.headers,
  };
}

// Convert a raw response body into a single-line human-readable error message.
// FastAPI error bodies come through as JSON with `{detail: string}` (HTTPException)
// or `{detail: Array<{type, loc, msg}>}` (422 validation) — never surface the
// raw array-as-object stringification (`[object Object]`) to the UI.
function readableErrorFromBody(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as { detail?: unknown };
    const detail = parsed?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (item && typeof item === 'object') {
            const entry = item as { msg?: unknown; loc?: unknown; type?: unknown };
            const loc = Array.isArray(entry.loc)
              ? entry.loc.filter((v) => v !== 'body' && v !== 'header' && v !== 'query').join('.')
              : '';
            const msg =
              typeof entry.msg === 'string'
                ? entry.msg
                : typeof entry.type === 'string'
                  ? entry.type
                  : JSON.stringify(item);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return typeof item === 'string' ? item : JSON.stringify(item);
        })
        .join('; ');
    }
    if (detail && typeof detail === 'object') return JSON.stringify(detail);
  } catch {
    // not JSON — fall through to raw text
  }
  return raw;
}

function parseSseBlock(rawBlock: string): EngineStreamEvent | null {
  const block = rawBlock.trim();
  if (!block) return null;

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim() || event;
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) return null;

  const rawData = dataLines.join('\n');
  try {
    return {
      event,
      data: JSON.parse(rawData),
    };
  } catch {
    return {
      event,
      data: rawData,
    };
  }
}

export interface StreamEngineChatParams {
  content: string;
  threadId?: string | null;
  modelOverride?: string | null;
  workspaceId?: string | null;
  signal?: AbortSignal;
  onEvent: (event: EngineStreamEvent) => void;
}

export async function streamEngineChat({
  content,
  threadId,
  modelOverride,
  workspaceId,
  signal,
  onEvent,
}: StreamEngineChatParams): Promise<void> {
  const response = await fetch(engineUrl('/chat/send'), {
    method: 'POST',
    headers: mergeHeaders(),
    body: JSON.stringify({
      content,
      thread_id: threadId || undefined,
      model_override: modelOverride || undefined,
      workspace_id: workspaceId || undefined,
    }),
    signal,
  });

  if (!response.ok) {
    const raw = await response.text().catch(() => response.statusText);
    const message = readableErrorFromBody(raw) || response.statusText;
    if (response.status === 401) {
      notifyUnauthorized(message);
    }
    throw new EngineHttpError(response.status, message);
  }

  if (!response.body) {
    throw new EngineHttpError(response.status, 'Engine returned no response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSseBlock(block);
      if (parsed) onEvent(parsed);
      boundary = buffer.indexOf('\n\n');
    }

    if (done) {
      const trailing = parseSseBlock(buffer);
      if (trailing) onEvent(trailing);
      break;
    }
  }
}
